import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { AsyncSearchSelect } from '@/components/AsyncSearchSelect';

const INDUSTRIES = ['E-commerce', 'Technology', 'Retail', 'Healthcare', 'Finance', 'Manufacturing', 'Education', 'Real Estate', 'Consulting', 'Media & Entertainment', 'Transportation', 'Hospitality', 'Telecommunications', 'Other'];
const EMPLOYEE_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
const SOURCES = ['Website', 'Contact Form', 'Get a Demo', 'Get a Call Back', 'LinkedIn', 'Referral', 'Cold Call', 'Email Campaign', 'Conference', 'Social Media', 'Self', 'Marketing', 'Internal Ref', 'Client Reference', 'Existing Client', 'Churned Client', 'Partner- India', 'Partner- Intl', 'OEM-VISPL', 'OEM-OXT', 'Other'];
const DESIGNATIONS = ['CEO', 'CTO', 'CFO', 'COO', 'VP Sales', 'VP Marketing', 'Director', 'Manager', 'Team Lead', 'Executive', 'Analyst', 'Other'];

function AddLead() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Check if user can assign leads (admin, managers, and senior roles)
  const canAssignLeads = ['admin', 'company_sales_head', 'country_sales_head', 'regional_sales_head', 'manager'].includes(user?.role);

  const [formData, setFormData] = useState({
    // Contact/Lead Info
    name: '',
    email: '',
    phone: '',
    designation: '',
    // Company Info
    organization: '',
    company_id: '',
    industry: '',
    employee_size: '',
    linkedin_website: '',
    location: '',
    description: '',
    // Lead Info
    source: '',
    status: 'new',
    interested: false,
    remarks: '',
    message: '',
    deal_value: '',
    deal_stage: '',
    lead_score: 0,
    win_probability: '',
    expected_close_date: '',
    // Options
    create_company: true,
    create_contact: true,
    // Assignment (for admin/manager)
    assign_to: '',
    assign_to_label: '',
    // NAD - Next Action Date (mandatory)
    next_action_date: '',
    next_action_note: '',
  });

  // Existing contacts belonging to the selected company — populated on company pick
  const [companyContacts, setCompanyContacts] = useState([]);
  const [loadingCompanyContacts, setLoadingCompanyContacts] = useState(false);
  const [selectedExistingContactId, setSelectedExistingContactId] = useState('');

  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: '',
    location: '',
    employee_count: '',
    annual_revenue: '',
    website: '',
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, []);

  // Fetch team members for assignment (senior roles only)
  const fetchTeamMembers = useCallback(async () => {
    if (!canAssignLeads) return;
    
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Filter to show assignable roles (all except admin and company heads)
      const assignableUsers = response.data.filter(u => 
        ['sdr', 'sales_executive', 'team_leader', 'manager', 'regional_sales_head', 'country_sales_head'].includes(u.role)
      );
      setTeamMembers(assignableUsers);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [canAssignLeads]);

  useEffect(() => {
    fetchCompanies();
    fetchContacts();
    fetchTeamMembers();
  }, [fetchCompanies, fetchContacts, fetchTeamMembers]);

  useEffect(() => {
    if (companySearch.length > 0) {
      const filtered = companies.filter(c => 
        c.name.toLowerCase().includes(companySearch.toLowerCase())
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(true);
    } else {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
  }, [companySearch, companies]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Map the backend-stored employee_count (which may be a raw number OR a bucket string)
  // to the bucket values that EMPLOYEE_SIZES expects.
  const toEmployeeSizeBucket = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '';
    const v = String(raw).trim();
    if (EMPLOYEE_SIZES.includes(v)) return v;
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return '';
    if (n <= 10) return '1-10';
    if (n <= 50) return '11-50';
    if (n <= 200) return '51-200';
    if (n <= 500) return '201-500';
    if (n <= 1000) return '501-1000';
    if (n <= 5000) return '1001-5000';
    return '5000+';
  };

  // Prefer the canonical casing from INDUSTRIES if the company's stored industry matches
  // case-insensitively; otherwise return the raw value so it's still preserved in the select.
  const normaliseIndustry = (raw) => {
    if (!raw) return '';
    const hit = INDUSTRIES.find((i) => i.toLowerCase() === String(raw).toLowerCase());
    return hit || String(raw);
  };

  const handleCompanySelect = async (company) => {
    setFormData((prev) => ({
      ...prev,
      organization: company.name,
      company_id: company.id,
      // Auto-fill from company record (Q2). Only overwrite if the user hasn't typed something.
      industry: prev.industry || normaliseIndustry(company.industry),
      employee_size: prev.employee_size || toEmployeeSizeBucket(company.employee_count ?? company.employee_size),
      linkedin_website: prev.linkedin_website || company.website || company.linkedin || '',
      location: prev.location || company.location || company.city || '',
      description: prev.description || company.description || '',
      // Phone at the lead level — only fill if the lead form's phone is empty
      phone: prev.phone || company.phone || '',
    }));
    setCompanySearch(company.name);
    setShowCompanyDropdown(false);
    setShowNewCompanyForm(false);
    // Reset any previously chosen existing contact since company changed
    setSelectedExistingContactId('');
    // Fetch existing contacts at this company for the picker (Q3)
    try {
      setLoadingCompanyContacts(true);
      const res = await axios.get(`${API}/contacts?company_id=${company.id}&limit=50`);
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      setCompanyContacts(list);
    } catch (err) {
      console.error('Failed to load company contacts:', err);
      setCompanyContacts([]);
    } finally {
      setLoadingCompanyContacts(false);
    }
  };

  const handleExistingContactSelect = (contactId) => {
    setSelectedExistingContactId(contactId);
    if (!contactId) return; // cleared
    const ct = companyContacts.find((c) => c.id === contactId);
    if (!ct) return;
    setFormData((prev) => ({
      ...prev,
      name: ct.name || prev.name,
      email: ct.email || prev.email,
      phone: ct.phone || prev.phone,
      designation: ct.designation || ct.role || prev.designation,
      // Don't auto-create a new contact on submit when user picks an existing one
      create_contact: false,
    }));
  };

  const handleCompanySearchChange = (e) => {
    const value = e.target.value;
    setCompanySearch(value);
    setFormData({
      ...formData,
      organization: value,
      company_id: '', // Clear company_id when typing
    });
    // Clear existing-contact suggestions since the selection is no longer valid
    if (companyContacts.length > 0) setCompanyContacts([]);
    if (selectedExistingContactId) setSelectedExistingContactId('');
  };

  const handleCreateNewCompany = () => {
    setShowNewCompanyForm(true);
    setNewCompany({
      ...newCompany,
      name: companySearch,
    });
    setShowCompanyDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Internal-domain blocklist — fail fast client-side
    const BLOCKED_DOMAINS = ['agami-tech.com', 'intalk.io', 'urs.com'];
    const emailDomain = (formData.email || '').split('@')[1]?.trim().toLowerCase();
    if (emailDomain && BLOCKED_DOMAINS.includes(emailDomain)) {
      setLoading(false);
      setMessage({
        type: 'error',
        text: `Emails from @${emailDomain} are internal and cannot be added as leads. Please use the prospect's business email.`,
      });
      return;
    }

    try {
      let companyId = formData.company_id;

      // Create new company if needed
      if (formData.create_company && !companyId && formData.organization && formData.organization.trim()) {
        // Check if company already exists (case-insensitive)
        const existingCompany = companies.find(
          c => c.name.toLowerCase() === formData.organization.toLowerCase().trim()
        );

        if (existingCompany) {
          companyId = existingCompany.id;
        } else {
          // Create new company with available data
          const companyData = {
            name: (showNewCompanyForm && newCompany.name) ? newCompany.name : formData.organization.trim(),
            industry: (showNewCompanyForm && newCompany.industry) ? newCompany.industry : formData.industry,
            location: showNewCompanyForm ? newCompany.location : '',
            employee_count: showNewCompanyForm ? newCompany.employee_count : '',
            annual_revenue: (showNewCompanyForm && newCompany.annual_revenue) ? parseFloat(newCompany.annual_revenue) : null,
            website: (showNewCompanyForm && newCompany.website) ? newCompany.website : formData.linkedin_website,
          };

          try {
            const companyResponse = await axios.post(`${API}/companies`, companyData);
            companyId = companyResponse.data.id;
          } catch (companyError) {
            console.error('Failed to create company:', companyError);
            // Continue without company if creation fails
          }
        }
      }

      // Create contact if needed (requires company)
      // If user picked an existing contact from the Company, reuse it — don't create a new one.
      let contactId = selectedExistingContactId || null;
      if (!contactId && formData.create_contact && formData.name && companyId) {
        const contactData = {
          company_id: companyId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          designation: formData.designation,
          is_primary: true,
          is_decision_maker: false,
        };
        
        try {
          const contactResponse = await axios.post(`${API}/contacts`, contactData);
          contactId = contactResponse.data.id;
        } catch (contactError) {
          console.error('Failed to create contact:', contactError);
          // Continue without contact if creation fails
        }
      }

      // Create lead
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        industry: formData.industry,
        employee_size: formData.employee_size,
        linkedin_website: formData.linkedin_website,
        source: formData.source,
        status: formData.status,
        interested: formData.interested,
        remarks: formData.remarks,
        message: formData.message,
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null,
        deal_stage: formData.deal_stage,
        lead_score: parseInt(formData.lead_score) || 0,
        win_probability: formData.win_probability ? parseInt(formData.win_probability) : null,
        expected_close_date: formData.expected_close_date || null,
        company_id: companyId,
        contact_id: contactId,
        next_action_date: formData.next_action_date || null,
        next_action_note: formData.next_action_note || null,
      };

      // If admin/manager is assigning to someone else
      if (canAssignLeads && formData.assign_to) {
        submitData.assigned_user_id = formData.assign_to;
      }

      const response = await axios.post(`${API}/leads`, submitData);
      
      let successMessage = 'Lead created successfully!';
      if (companyId && !formData.company_id) {
        successMessage += ' Company added.';
      }
      if (contactId) {
        successMessage += ' Contact created.';
      }
      
      setMessage({ type: 'success', text: successMessage });
      setTimeout(() => {
        navigate(`/leads/${response.data.id}`);
      }, 1500);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to create lead. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full" data-testid="add-lead-page">
      <div className="mb-6">
        <h1 className="page-title">Add New Lead</h1>
        <p className="page-subtitle">Create a new lead with company and contact</p>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm">
        {message.text && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
            }`}
            role="alert"
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact/Lead Person Information */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
              Contact Person Details
            </h3>

            {/* Existing-contact picker (Q3) — only shown when a Company is selected */}
            {formData.company_id && (companyContacts.length > 0 || loadingCompanyContacts) && (
              <div
                className="mb-4 p-3 rounded-lg bg-blue-50/60 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                data-testid="existing-contact-picker"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      Pick an existing contact at <span className="text-blue-700 dark:text-blue-400">{formData.organization}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Auto-fills name, email, phone, designation. Leave blank to create a new contact.
                    </p>
                  </div>
                  {selectedExistingContactId && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedExistingContactId('');
                        setFormData((prev) => ({ ...prev, create_contact: true }));
                      }}
                      className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
                      data-testid="clear-existing-contact-btn"
                    >
                      Clear selection
                    </button>
                  )}
                </div>
                {loadingCompanyContacts ? (
                  <p className="text-xs text-slate-500 italic">Loading contacts…</p>
                ) : (
                  <select
                    value={selectedExistingContactId}
                    onChange={(e) => handleExistingContactSelect(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                    data-testid="existing-contact-select"
                  >
                    <option value="">— Create new contact —</option>
                    {companyContacts.map((ct) => (
                      <option key={ct.id} value={ct.id}>
                        {ct.name}
                        {ct.designation ? ` · ${ct.designation}` : ''}
                        {ct.email ? ` · ${ct.email}` : ''}
                        {ct.is_primary ? '  ★ Primary' : ''}
                        {ct.is_decision_maker ? '  ★ DM' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="name" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="John Doe"
                  data-testid="name-input"
                />
              </div>

              <div>
                <label htmlFor="designation" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Designation / Role
                </label>
                <select
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                  data-testid="designation-select"
                >
                  <option value="">Select Designation</option>
                  {DESIGNATIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="john@example.com"
                  data-testid="email-input"
                />
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Required — a Contact is auto-created from this email.
                </p>
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="+91 98765 43210"
                  data-testid="phone-input"
                />
              </div>

              <div className="lg:col-span-4 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg p-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="create_contact"
                    checked={formData.create_contact}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                    data-testid="create-contact-checkbox"
                  />
                  <span className="text-sm text-slate-800 dark:text-slate-200">
                    <strong>Create Contact:</strong> Also add this person to the Contacts page
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
              Company / Organization
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2 relative">
                <label htmlFor="organization" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  id="organization"
                  value={companySearch}
                  onChange={handleCompanySearchChange}
                  onFocus={() => companySearch.length > 0 && setShowCompanyDropdown(true)}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="Start typing to search or create new company..."
                  data-testid="organization-input"
                  autoComplete="off"
                />
                
                {/* Company Dropdown */}
                {showCompanyDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCompanies.length > 0 ? (
                      <>
                        {filteredCompanies.map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => handleCompanySelect(company)}
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-between"
                            data-testid={`company-option-${company.id}`}
                          >
                            <div>
                              <p className="font-medium text-sm text-slate-900 dark:text-white">{company.name}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{company.industry}</p>
                            </div>
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">Existing</span>
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={handleCreateNewCompany}
                          className="w-full px-3 py-2 text-left hover:bg-yellow-50 dark:hover:bg-yellow-900/20 border-t border-slate-200 dark:border-slate-700 flex items-center"
                        >
                          <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-sm text-yellow-700 dark:text-yellow-400">Create new company: <strong>"{companySearch}"</strong></span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={handleCreateNewCompany}
                        className="w-full px-3 py-2 text-left hover:bg-yellow-50 dark:hover:bg-yellow-900/20 flex items-center"
                      >
                        <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-sm text-yellow-700 dark:text-yellow-400">Create new company: <strong>"{companySearch}"</strong></span>
                      </button>
                    )}
                  </div>
                )}

                {formData.company_id && (
                  <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Linked to existing company
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="industry" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Industry
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={(e) => {
                    handleChange(e);
                    setNewCompany({ ...newCompany, industry: e.target.value });
                  }}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                  data-testid="industry-select"
                >
                  <option value="">Select Industry</option>
                  {INDUSTRIES.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                  {/* Preserve a non-standard industry value loaded from the Company record
                      (e.g. "SaaS") so the user still sees what was pre-filled. */}
                  {formData.industry && !INDUSTRIES.some(i => i.toLowerCase() === formData.industry.toLowerCase()) && (
                    <option key={`custom-${formData.industry}`} value={formData.industry}>{formData.industry}</option>
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="employee_size" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Employee Size
                </label>
                <select
                  id="employee_size"
                  name="employee_size"
                  value={formData.employee_size}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                  data-testid="employee-size-select"
                >
                  <option value="">Select Size</option>
                  {EMPLOYEE_SIZES.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              {/* New Company Form */}
              {showNewCompanyForm && (
                <div className="lg:col-span-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm text-yellow-800 dark:text-yellow-300">New Company Details</h4>
                    <button
                      type="button"
                      onClick={() => setShowNewCompanyForm(false)}
                      className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Company Name</label>
                      <input
                        type="text"
                        value={newCompany.name}
                        onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Location</label>
                      <input
                        type="text"
                        value={newCompany.location}
                        onChange={(e) => setNewCompany({ ...newCompany, location: e.target.value })}
                        placeholder="Mumbai, India"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Employee Count</label>
                      <input
                        type="text"
                        value={newCompany.employee_count}
                        onChange={(e) => setNewCompany({ ...newCompany, employee_count: e.target.value })}
                        placeholder="50-100"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Website</label>
                      <input
                        type="url"
                        value={newCompany.website}
                        onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value })}
                        placeholder="https://company.com"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="lg:col-span-2">
                <label htmlFor="linkedin_website" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  LinkedIn / Website
                </label>
                <input
                  type="url"
                  id="linkedin_website"
                  name="linkedin_website"
                  value={formData.linkedin_website}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="https://linkedin.com/company/..."
                  data-testid="linkedin-input"
                />
              </div>

              <div className="lg:col-span-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="create_company"
                    checked={formData.create_company}
                    onChange={handleChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                    data-testid="create-company-checkbox"
                  />
                  <span className="text-sm text-green-800 dark:text-green-300">
                    <strong>Create Company:</strong> Auto-add to Companies page if it doesn't exist
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Lead Source & Status */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
              Lead Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="source" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Source</label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                  data-testid="source-select"
                >
                  <option value="">Select Source</option>
                  {SOURCES.map((source) => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="status" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                  data-testid="status-select"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="meeting">Meeting</option>
                  <option value="closed_won">Closed Won</option>
                  <option value="closed_lost">Closed Lost</option>
                </select>
              </div>

              {/* Assign To - Only visible to Admin/Manager */}
              {canAssignLeads && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Assign To
                    <span className="text-xs text-slate-400 dark:text-slate-500 ml-1">(Optional)</span>
                  </label>
                  <AsyncSearchSelect
                    value={formData.assign_to}
                    valueLabel={formData.assign_to_label}
                    onSelect={(u) => {
                      if (!u) setFormData(prev => ({ ...prev, assign_to: '', assign_to_label: '' }));
                      else setFormData(prev => ({ ...prev, assign_to: u.id, assign_to_label: `${u.name || u.email}${u.role ? ` (${u.role})` : ''}` }));
                    }}
                    fetchOptions={async (q) => {
                      const qLower = (q || '').toLowerCase();
                      if (!qLower) return teamMembers.slice(0, 20);
                      return teamMembers.filter(u =>
                        (u.name || '').toLowerCase().includes(qLower)
                        || (u.email || '').toLowerCase().includes(qLower)
                        || (u.role || '').toLowerCase().includes(qLower)
                      ).slice(0, 20);
                    }}
                    placeholder="Assign to myself, or search teammate…"
                    testId="assign-to-search"
                    minChars={0}
                    debounceMs={150}
                    renderOption={(u) => (
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900 dark:text-white truncate">{u.name || u.email}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {u.email}{u.role ? ` · ${u.role}` : ''}
                        </span>
                      </div>
                    )}
                  />
                </div>
              )}

              <div className="flex items-end pb-1">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="interested"
                    checked={formData.interested}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                    data-testid="interested-checkbox"
                  />
                  <span className="text-sm text-slate-900 dark:text-white">Mark as Interested</span>
                </label>
              </div>
            </div>
          </div>

          {/* Deal Information */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
              Deal Information (INR)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="deal_value" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Deal Value (₹)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-sm text-slate-500 dark:text-slate-400">₹</span>
                  <input
                    type="number"
                    id="deal_value"
                    name="deal_value"
                    value={formData.deal_value}
                    onChange={handleChange}
                    className="w-full pl-7 pr-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    placeholder="100000"
                    data-testid="deal-value-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="win_probability" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Win Probability (%)</label>
                <input
                  type="number"
                  id="win_probability"
                  name="win_probability"
                  min="0"
                  max="100"
                  value={formData.win_probability}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="50"
                  data-testid="probability-input"
                />
              </div>

              <div>
                <label htmlFor="expected_close_date" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Expected Close Date</label>
                <input
                  type="date"
                  id="expected_close_date"
                  name="expected_close_date"
                  value={formData.expected_close_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                  data-testid="expected-close-date-input"
                />
              </div>

              {/* NAD - Next Action Date */}
              <div className="col-span-2 sm:col-span-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="next_action_date" className="block text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">
                      Next Action Date (NAD) *
                    </label>
                    <input
                      type="date"
                      id="next_action_date"
                      name="next_action_date"
                      value={formData.next_action_date}
                      onChange={handleChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white"
                      data-testid="nad-date-input"
                    />
                    <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">When is the next planned action?</p>
                  </div>
                  <div>
                    <label htmlFor="next_action_note" className="block text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">
                      Planned Action
                    </label>
                    <input
                      type="text"
                      id="next_action_note"
                      name="next_action_note"
                      value={formData.next_action_note}
                      onChange={handleChange}
                      placeholder="e.g., Follow-up call, Send proposal..."
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder-slate-400"
                      data-testid="nad-note-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="lead_score" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Lead Score (0-100)</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="range"
                    id="lead_score"
                    name="lead_score"
                    min="0"
                    max="100"
                    value={formData.lead_score}
                    onChange={handleChange}
                    className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    data-testid="lead-score-input"
                  />
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 w-10 text-center">{formData.lead_score}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">Additional Notes</h3>
            <div>
              <label htmlFor="remarks" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="2"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                placeholder="Add any internal notes..."
                data-testid="remarks-input"
              ></textarea>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={() => navigate('/leads')}
              className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              data-testid="submit-button"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Lead'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLead;
